/**
 * `<MessageLine>` — one row in the B-1 VM Debug pane message stream.
 *
 * Renders a single `VmMessage` as a compact one-line summary. When
 * `expanded === true`, additionally renders the raw envelope /
 * auditRecord under the hold-to-reveal gate — `rawActive` controls
 * whether the JSON payload is visible or replaced with a "Hold Alt
 * to reveal" placeholder.
 *
 * The reveal gate is the load-bearing security property. With
 * `rawActive === false` we render the SHAPE of the payload (the
 * placeholder) but NOT the payload itself. The watermark is a real
 * absolutely-positioned `<div>` (rendered by the parent
 * `<RawRevealOverlay>`) so screenshot tools capture the redact
 * disclaimer in the same frame as the revealed payload.
 */

import { useRef, useEffect } from 'react';
import type { VmMessage } from '@volley/crucible-ipc-types/vm-message-stream';

const DIRECTION_GLYPH = {
  sent: '→',
  received: '←',
} as const;

const KIND_TINT: Record<VmMessage['kind'], string> = {
  request: 'text-sky-300/80',
  response: 'text-emerald-300/80',
  'stream-chunk': 'text-emerald-200/60',
  'stream-end': 'text-emerald-300/80',
  'audit-emit': 'text-violet-300/80',
  heartbeat: 'text-white/30',
  lifecycle: 'text-amber-300/80',
};

const OUTCOME_TINT: Record<NonNullable<VmMessage['outcome']>, string> = {
  ok: 'text-white/60',
  error: 'text-red-300',
  cancelled: 'text-orange-300',
};

export interface MessageLineProps {
  message: VmMessage;
  /** True when this row's expand toggle is on. Parent owns expansion
   *  state so the virtualised list can remeasure heights. */
  expanded: boolean;
  /** Called when the user clicks the row's expand chevron. */
  onToggleExpanded: () => void;
  /** True when the hold-to-reveal gate is active (toggleArmed && Alt
   *  held). When false, raw payloads are replaced with a placeholder. */
  rawActive: boolean;
  /** Optional ref callback fired with the row's DOM element so the
   *  virtualised list can measure the height after first render. */
  measureRef?: (el: HTMLDivElement | null) => void;
}

export function MessageLine({
  message,
  expanded,
  onToggleExpanded,
  rawActive,
  measureRef,
}: MessageLineProps): React.JSX.Element {
  const innerRef = useRef<HTMLDivElement | null>(null);

  // Forward the row's DOM element to the parent's measure callback.
  // Using a useEffect rather than passing the ref through directly
  // because the parent's callback may not exist yet at first render.
  useEffect(() => {
    if (measureRef) {
      measureRef(innerRef.current);
    }
    return () => {
      if (measureRef) {
        measureRef(null);
      }
    };
  }, [measureRef, expanded]);

  return (
    <div
      ref={innerRef}
      data-testid="vm-debug-message-line"
      data-seq={message.seq}
      data-kind={message.kind}
      data-direction={message.direction}
      data-expanded={expanded}
      className="border-b border-white/5 px-3 py-1 font-mono text-[11px]"
    >
      <div className="flex items-baseline gap-2">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse message details' : 'Expand message details'}
          className="text-white/40 hover:text-white/70"
        >
          {expanded ? '▾' : '▸'}
        </button>
        <span className="text-white/30 tabular-nums">#{message.seq}</span>
        <span aria-hidden="true" className="text-white/40">
          {DIRECTION_GLYPH[message.direction]}
        </span>
        <span className={`${KIND_TINT[message.kind]} font-medium`}>
          {message.method}
        </span>
        {message.requestIdSurrogate ? (
          <span className="text-white/30">
            req_{message.requestIdSurrogate}
          </span>
        ) : null}
        {message.outcome ? (
          <span className={OUTCOME_TINT[message.outcome]}>
            {message.outcome}
          </span>
        ) : null}
        {message.durationMs !== undefined ? (
          <span className="text-white/40">{message.durationMs}ms</span>
        ) : null}
        <span className="text-white/60">{message.summary}</span>
      </div>
      {expanded ? (
        <RawPayloadBlock message={message} rawActive={rawActive} />
      ) : null}
    </div>
  );
}

interface RawPayloadBlockProps {
  message: VmMessage;
  rawActive: boolean;
}

function RawPayloadBlock({
  message,
  rawActive,
}: RawPayloadBlockProps): React.JSX.Element {
  // Branch on rawActive — this is the security property. When false,
  // we render the placeholder + the shape (which fields are present)
  // but NEVER the payload contents.
  if (!rawActive) {
    return (
      <div
        data-testid="vm-debug-raw-payload-placeholder"
        className="mt-1 ml-7 rounded border border-dashed border-white/10 bg-black/30 px-2 py-1 text-[10px] text-white/30"
      >
        Hold Alt to reveal raw payload
        {message.envelope !== undefined ? ' (envelope present)' : ''}
        {message.auditRecord !== undefined ? ' (audit record present)' : ''}
      </div>
    );
  }
  return (
    <div
      data-testid="vm-debug-raw-payload"
      className="mt-1 ml-7 rounded border border-amber-500/40 bg-black/60 px-2 py-1"
    >
      <div className="mb-1 text-[10px] uppercase text-amber-300/80">
        Raw payload — internal only
      </div>
      {message.envelope !== undefined ? (
        <pre
          data-testid="vm-debug-raw-envelope"
          className="overflow-x-auto whitespace-pre-wrap break-words text-[10px] text-white/80"
        >
          envelope: {safeStringify(message.envelope)}
        </pre>
      ) : null}
      {message.auditRecord !== undefined ? (
        <pre
          data-testid="vm-debug-raw-audit-record"
          className="overflow-x-auto whitespace-pre-wrap break-words text-[10px] text-white/80"
        >
          auditRecord: {safeStringify(message.auditRecord)}
        </pre>
      ) : null}
      {message.envelope === undefined && message.auditRecord === undefined ? (
        <div className="text-[10px] text-white/40">(no raw payload)</div>
      ) : null}
    </div>
  );
}

/** Defensive JSON.stringify — never throws on cycles. */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[unserialisable]';
  }
}
